<template>
  <div class="page-wrapper">
    <div class="content container-fluid">
      <div class="row">
        <!-- Sidebar Folders -->
        <div class="col-md-3">
          <div class="card sidebar-card shadow-sm mb-3">
            <div class="card-header d-flex justify-content-between align-items-center bg-light">
              <span>Folders</span>
            </div>
            <div class="card-body p-2 sidebar-body">
              <input type="text" class="form-control form-control-sm mb-2" placeholder="Search folder" v-model="folderSearch">
              <ul class="list-group list-group-flush folder-list">
                <li class="list-group-item folder-item" v-for="tab in filteredFolders" :key="tab.id"
                    :class="{'active': currentTabId === tab.id}" @click="currentTabId = tab.id">
                  {{ tab.name }}
                </li>
              </ul>
              <div class="d-flex mt-2">
                <input v-model="newFolderName" class="form-control form-control-sm mr-1" placeholder="Tên folder mới">
                <button class="btn btn-sm btn-primary" @click="createNewFolder">Tạo</button>
                <button class="btn btn-sm btn-danger ml-1" @click="deleteFolder" :disabled="images.length>0">Xóa</button>
              </div>
            </div>
          </div>
        </div>

        <!-- Main Content -->
        <div class="col-md-9">
          <div class="card shadow-sm mb-3">
            <div class="card-header d-flex justify-content-between align-items-center bg-light">
              <span>Media Library</span>
              <div>
                <input type="file" class="d-none" ref="fileInput" @change="handleFileUpload" multiple>
                <button class="btn btn-sm btn-success" @click="$refs.fileInput.click()">
                  <i class="las la-upload"></i> Upload
                </button>
              </div>
            </div>

            <div class="card-body">
              <!-- Search -->
              <div class="d-flex mb-2">
                <input type="text" class="form-control form-control-sm" placeholder="Search file" v-model="imageSearch">
              </div>

              <div v-if="loading" class="text-center py-3">Đang tải...</div>
              <div v-else class="image-grid">
                <div class="row">
                  <div class="col-6 col-sm-4 col-md-3 mb-3" v-for="file in paginatedImages" :key="file.url">
                    <div class="card image-card h-100 position-relative">

                      <!-- Quick actions -->
                      <div class="quick-actions">
                        <button class="btn btn-xs btn-outline-secondary mr-1"
                                @click.stop="downloadFile(file)"
                                :disabled="isDownloading(file.url)">
                          <i class="las la-download"></i>
                        </button>
                        <button class="btn btn-xs btn-outline-secondary"
                                @click.stop="copyLink(file.url)">
                          <i class="las la-link"></i>
                        </button>
                      </div>

                      <!-- Image -->
                      <template v-if="getFileType(file.url)==='image'">
                        <img :src="file.url" class="card-img-top img-hover" :alt="file.title" @click="viewFile(file)">
                      </template>

                      <!-- Video -->
                      <template v-else-if="getFileType(file.url)==='video'">
                        <video class="card-img-top video-preview" controls @click="viewFile(file)">
                          <source :src="file.url" type="video/mp4" />
                        </video>
                      </template>

                      <!-- PDF -->
                      <template v-else-if="getFileType(file.url)==='pdf'">
                        <div class="file-preview" @click="downloadFile(file)">
                          <i class="las la-file-pdf la-3x text-danger" title="Tải PDF"></i>
                        </div>
                      </template>

                      <!-- DOCX -->
                      <template v-else-if="getFileType(file.url)==='docx'">
                        <div class="file-preview" @click="downloadFile(file)">
                          <i class="las la-file-word la-3x text-primary" title="Tải Word"></i>
                        </div>
                      </template>

                      <!-- EXCEL -->
                      <template v-else-if="getFileType(file.url)==='excel'">
                        <div class="file-preview" @click="downloadFile(file)">
                          <i class="las la-file-excel la-3x text-success" title="Tải Excel"></i>
                        </div>
                      </template>

                      <!-- Other -->
                      <template v-else>
                        <div class="file-preview" @click="downloadFile(file)">
                          <i class="las la-file la-3x text-secondary" title="Tải File"></i>
                        </div>
                      </template>

                      <!-- Progress overlay -->
                      <div class="download-overlay" v-if="isDownloading(file.url)">
                        <div class="progress w-100">
                          <div class="progress-bar" role="progressbar"
                               :style="{ width: (downloading[file.url] || 0) + '%' }"
                               :aria-valuenow="downloading[file.url] || 0" aria-valuemin="0" aria-valuemax="100">
                            {{ (downloading[file.url] || 0) }}%
                          </div>
                        </div>
                      </div>

                      <!-- Info -->
                      <div class="card-body p-2">
                        <h6 class="card-title text-truncate mb-1">{{ file.title }}</h6>
                        <small class="text-muted">{{ formatSize(file.size) }}</small>
                      </div>
                      <div class="card-footer p-1 text-center">
                        <small class="text-muted">{{ formatDate(file.uploadedAt) }}</small>
                      </div>
                      <button class="btn btn-sm btn-danger delete-btn" @click.stop="deleteImage(file)">&times;</button>
                    </div>
                  </div>
                </div>

                <!-- Pagination -->
                <nav v-if="totalPages>1">
                  <ul class="pagination justify-content-center">
                    <li class="page-item" :class="{disabled: currentPage===1}">
                      <a class="page-link" href="#" @click.prevent="currentPage = Math.max(1, currentPage-1)">Trước</a>
                    </li>
                    <li class="page-item" v-for="page in totalPages" :key="page" :class="{active: currentPage===page}">
                      <a class="page-link" href="#" @click.prevent="currentPage=page">{{ page }}</a>
                    </li>
                    <li class="page-item" :class="{disabled: currentPage===totalPages}">
                      <a class="page-link" href="#" @click.prevent="currentPage = Math.min(totalPages, currentPage+1)">Sau</a>
                    </li>
                  </ul>
                </nav>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal cho Image + Video -->
    <div class="modal fade show" tabindex="-1" 
         v-if="selectedFile && ['image','video'].includes(getFileType(selectedFile.url))" 
         style="display: block;">
      <div class="modal-dialog modal-dialog-centered modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">{{ selectedFile.title }}</h5>
            <button type="button" class="close" @click="closeModal">&times;</button>
          </div>
          <div class="modal-body p-0 text-center">
            <template v-if="getFileType(selectedFile.url)==='image'">
              <img :src="selectedFile.url" class="img-fluid w-100">
            </template>
            <template v-else-if="getFileType(selectedFile.url)==='video'">
              <video class="modal-video" controls autoplay>
                <source :src="selectedFile.url" type="video/mp4" />
              </video>
            </template>
          </div>
          <div class="modal-footer justify-content-between">
            <button type="button" class="btn btn-outline-secondary" @click="copyLink(selectedFile.url)">
              <i class="las la-link"></i> Copy link
            </button>
            <div>
              <button type="button" class="btn btn-primary mr-2" @click="downloadFile(selectedFile)" :disabled="isDownloading(selectedFile.url)">
                <i class="las la-download"></i> Tải xuống
              </button>
              <button type="button" class="btn btn-secondary" @click="closeModal">Đóng</button>
            </div>
          </div>
        </div>
      </div>
      <div class="modal-backdrop fade show" @click="closeModal"></div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import api from '@/api';

const folderTabs = ref([]);
const currentTabId = ref(null);
const images = ref([]);
const loading = ref(true);
const newFolderName = ref('');
const selectedFile = ref(null);
const folderSearch = ref('');
const imageSearch = ref('');
const currentPage = ref(1);
const perPage = 12;

// track download progress per URL
const downloading = ref({}); // { [url]: percent }

// File type helper
const getFileType = (url) => {
  if (!url) return 'unknown';
  const clean = url.split('?')[0]; // bỏ query
  const ext = clean.split('.').pop().toLowerCase();
  if (['jpg','jpeg','png','gif','webp','svg'].includes(ext)) return 'image';
  if (['mp4','webm','ogg','avi','mov'].includes(ext)) return 'video';
  if (['pdf'].includes(ext)) return 'pdf';
  if (['doc','docx'].includes(ext)) return 'docx';
  if (['xls','xlsx','csv'].includes(ext)) return 'excel';
  return 'other';
};

const isDownloading = (url) => Object.prototype.hasOwnProperty.call(downloading.value, url);

// Prefer a nice filename
const preferredFilename = (file) => {
  const urlPart = file.url.split('/').pop().split('?')[0] || 'file';
  const urlExt = urlPart.includes('.') ? urlPart.substring(urlPart.lastIndexOf('.')+1) : '';
  const safeTitle = (file.title || urlPart).replace(/[\\/:*?"<>|]/g, '_').trim();
  if (urlExt && !safeTitle.toLowerCase().endsWith('.'+urlExt.toLowerCase())) {
    return `${safeTitle}.${urlExt}`;
  }
  return safeTitle || 'file';
};

// Computed
const currentTab = computed(() => folderTabs.value.find(f => f.id === currentTabId.value) || {});
const filteredFolders = computed(() => folderTabs.value.filter(f => f.name.toLowerCase().includes(folderSearch.value.toLowerCase())));
const filteredImages = computed(() => {
  if (!imageSearch.value.trim()) return images.value;
  return images.value.filter(img => (img.title || '').toLowerCase().includes(imageSearch.value.toLowerCase()));
});
const paginatedImages = computed(() => {
  const start = (currentPage.value - 1) * perPage;
  return filteredImages.value.slice(start, start + perPage);
});
const totalPages = computed(() => Math.ceil(filteredImages.value.length / perPage));

watch(imageSearch, () => { currentPage.value = 1; });

const fetchFolders = async () => {
  try {
    const res = await api.get('/api/images/folders');
    folderTabs.value = res.data.map(f => ({ id: f, name: f, folderName: f }));
    if (!currentTabId.value) currentTabId.value = folderTabs.value[0]?.id;
  } catch (err) { console.error(err); folderTabs.value = []; }
};

const fetchImages = async folderName => {
  if (!folderName) return;
  loading.value = true;
  currentPage.value = 1;
  try {
    const res = await api.get(`/api/images/list/${folderName}`);
    images.value = res.data.sort((a, b) => new Date(b.uploadedAt) - new Date(a.uploadedAt));
  } catch (err) { console.error(err); images.value = []; }
  finally { loading.value = false; }
};

const handleFileUpload = async e => {
  const files = e.target.files;
  if (!files?.length) return;
  try {
    const formData = new FormData();
    for (const f of files) formData.append('files', f);
    formData.append('folderName', currentTab.value.folderName);
    await api.post('/api/images/upload', formData, { headers: { 'Content-Type':'multipart/form-data' } });
    fetchImages(currentTab.value.folderName);
  } catch (err) { console.error(err); }
};

const deleteImage = async file => {
  if (!window.confirm(`Bạn có chắc chắn muốn xóa "${file.title}"?`)) return;
  const folderName = currentTab.value.folderName;
  const filename = file.url.substring(file.url.lastIndexOf('/')+1).split('?')[0];
  try {
    await api.delete(`/api/images/${folderName}/${filename}`);
    fetchImages(folderName);
  } catch (err) { console.error(err); }
};

const createNewFolder = async () => {
  if (!newFolderName.value.trim()) return;
  try { 
    await api.post(`/api/images/create-folder?folderName=${encodeURIComponent(newFolderName.value.trim())}`);
    await fetchFolders(); 
    newFolderName.value=''; 
  } catch(err){ console.error(err); }
};

const deleteFolder = async () => {
  if (images.value.length>0) return;
  if (!window.confirm(`Bạn có chắc chắn muốn xóa folder "${currentTab.value.name}"?`)) return;
  try { await api.delete(`/api/images/delete-folder/${currentTab.value.folderName}`); await fetchFolders(); } 
  catch(err){ console.error(err); }
};

// FE-only download: same-origin => <a download>; cross-origin => blob fallback (with progress)
const downloadFile = async (file) => {
  const url = file.url;
  const filename = preferredFilename(file);
  try {
    const target = new URL(url, window.location.href);
    const sameOrigin = target.origin === window.location.origin;

    if (sameOrigin) {
      // Browser handles streaming, no RAM spike
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      return;
    }

    // Cross-origin fallback: fetch blob (needs CORS allowed)
    downloading.value[url] = 0;
    const res = await api.get(url, {
      responseType: 'blob',
      onDownloadProgress: (e) => {
        if (e.total) downloading.value[url] = Math.round((e.loaded * 100) / e.total);
      }
    });
    const blobUrl = URL.createObjectURL(res.data);
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(blobUrl);
  } catch (err) {
    console.error('Download error, opening in new tab as fallback', err);
    // Last resort
    window.open(url, '_blank', 'noopener');
  } finally {
    delete downloading.value[url];
  }
};

const copyLink = async (url) => {
  try {
    await navigator.clipboard.writeText(url);
    // optionally toast
  } catch (e) {
    console.error(e);
  }
};

// Modal
const viewFile = file => { 
  const type = getFileType(file.url);
  if (['image','video'].includes(type)) {
    selectedFile.value = file;
  }
};
const closeModal = () => { selectedFile.value = null; };

const formatSize = size => size?(size>1024?(size>1024*1024?Math.round(size/1024/1024)+'mb':Math.round(size/1024)+'kb'):size+'b'):'';
const formatDate = dateStr => new Date(dateStr).toLocaleString();

watch(currentTabId, id => {
  const tab = folderTabs.value.find(f => f.id===id);
  if(tab) fetchImages(tab.folderName);
}, { immediate:true });

onMounted(() => fetchFolders());
</script>

<style scoped>
.sidebar-card { max-height: 80vh; overflow-y: auto; }
.folder-item.active { background-color: #007bff; color: #fff; cursor: pointer; }
.folder-item { cursor: pointer; }
.image-card { cursor: pointer; transition: transform 0.2s; }
.image-card:hover { transform: scale(1.03); z-index: 2; }

.img-hover { object-fit: cover; height: 150px; width: 100%; }
.video-preview { object-fit: cover; height: 150px; width: 100%; cursor: pointer; }
.modal-video { width: 100%; max-height: 80vh; background: black; }

.file-preview {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 150px;
  border: 1px dashed #ddd;
  cursor: pointer;
}

.quick-actions {
  position: absolute;
  top: 6px; left: 6px;
  z-index: 3;
  display: flex;
}
.btn.btn-xs { padding: 2px 6px; font-size: 12px; line-height: 1.2; }

.download-overlay {
  position: absolute;
  left: 0; right: 0; bottom: 0;
  padding: 6px;
  background: rgba(255,255,255,0.85);
  z-index: 4;
}

.delete-btn { position: absolute; top:5px; right:5px; }
.modal-backdrop { background-color: rgba(0,0,0,0.5); }
.pagination .page-item .page-link { cursor: pointer; }
.modal.show .modal-dialog {
    -webkit-transform: none;
    transform: none;
    z-index: 9999999;
}
</style>
